import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  MenuItem,
  CircularProgress,
  Tabs,
  Tab,
  Avatar,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Chip,
} from '@mui/material';
import {
  PhotoCamera as PhotoCameraIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuthStore } from '../store/authStore';
import networkService from '../services/network.service';
import { TeacherNetworkRequest, User } from '../types';
import api from '../services/api';
import OrganizationTab from '../components/OrganizationTab';

// Common timezones with Pacific/Noumea
const TIMEZONES = [
  { value: 'Pacific/Noumea', label: 'Nouvelle-Calédonie (Nouméa)' },
  { value: 'Europe/Paris', label: 'France (Paris)' },
  { value: 'America/New_York', label: 'États-Unis (New York)' },
  { value: 'America/Los_Angeles', label: 'États-Unis (Los Angeles)' },
  { value: 'Asia/Tokyo', label: 'Japon (Tokyo)' },
  { value: 'Australia/Sydney', label: 'Australie (Sydney)' },
  { value: 'Pacific/Auckland', label: 'Nouvelle-Zélande (Auckland)' },
  { value: 'UTC', label: 'UTC' },
];

// Supported currencies
const CURRENCIES = [
  { value: 'xpf', label: 'XPF - Franc Pacifique' },
  { value: 'eur', label: 'EUR - Euro' },
  { value: 'usd', label: 'USD - Dollar américain' },
  { value: 'aud', label: 'AUD - Dollar australien' },
  { value: 'sgd', label: 'SGD - Dollar singapourien' },
  { value: 'nzd', label: 'NZD - Dollar néo-zélandais' },
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`account-tabpanel-${index}`}
      aria-labelledby={`account-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const Account: React.FC = () => {
  const { user, updateProfile, refreshProfile, isLoading } = useAuthStore();
  const [tabValue, setTabValue] = useState(0);
  const [timezone, setTimezone] = useState(user?.timezone || 'Pacific/Noumea');
  const [cameraIp, setCameraIp] = useState(user?.camera_ip || '');
  const [taxRate, setTaxRate] = useState(user?.tax_rate?.toString() || '6');
  const [currency, setCurrency] = useState<'xpf' | 'eur' | 'usd' | 'aud' | 'sgd' | 'nzd'>(user?.currency || 'xpf');
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Network state
  const [receivedRequests, setReceivedRequests] = useState<TeacherNetworkRequest[]>([]);
  const [sentRequests, setSentRequests] = useState<TeacherNetworkRequest[]>([]);
  const [connections, setConnections] = useState<User[]>([]);
  const [loadingNetwork, setLoadingNetwork] = useState(false);

  // Refresh profile data when component mounts
  useEffect(() => {
    refreshProfile().catch(err => {
      console.error('Error refreshing profile:', err);
    });
  }, [refreshProfile]);

  // Update form fields when user data changes
  useEffect(() => {
    if (user) {
      setTimezone(user.timezone || 'Pacific/Noumea');
      setCameraIp(user.camera_ip || '');
      setTaxRate(user.tax_rate?.toString() || '6');
      setCurrency(user.currency || 'xpf');
    }
  }, [user]);

  // Load network data when tab changes to network
  useEffect(() => {
    if (tabValue === 1) {
      loadNetworkData();
    }
  }, [tabValue]);

  const loadNetworkData = async () => {
    setLoadingNetwork(true);
    try {
      const [received, sent, conns] = await Promise.all([
        networkService.getReceivedRequests(),
        networkService.getSentRequests(),
        networkService.getConnections(),
      ]);
      setReceivedRequests(received);
      setSentRequests(sent);
      setConnections(conns);
    } catch (error) {
      console.error('Error loading network data:', error);
      toast.error('Erreur lors du chargement des données du réseau');
    } finally {
      setLoadingNetwork(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;

    setIsSaving(true);
    try {
      await updateProfile({
        timezone,
        camera_ip: cameraIp,
        tax_rate: parseFloat(taxRate),
        currency: currency as 'xpf' | 'eur' | 'usd' | 'aud' | 'sgd' | 'nzd'
      });
      toast.success('Paramètres mis à jour avec succès');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Erreur lors de la mise à jour des paramètres');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('L\'image ne doit pas dépasser 5 Mo');
      return;
    }

    setIsUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('profile_photo', file);

      await api.patch('/auth/profile/update/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      await refreshProfile();
      toast.success('Photo de profil mise à jour');
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Erreur lors de l\'upload de la photo');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleAcceptRequest = async (requestId: number) => {
    try {
      await networkService.acceptRequest(requestId);
      toast.success('Demande acceptée');
      loadNetworkData();
    } catch (error) {
      console.error('Error accepting request:', error);
      toast.error('Erreur lors de l\'acceptation de la demande');
    }
  };

  const handleRejectRequest = async (requestId: number) => {
    try {
      await networkService.rejectRequest(requestId);
      toast.success('Demande refusée');
      loadNetworkData();
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error('Erreur lors du refus de la demande');
    }
  };

  const handleCancelRequest = async (requestId: number) => {
    try {
      await networkService.cancelRequest(requestId);
      toast.success('Demande annulée');
      loadNetworkData();
    } catch (error) {
      console.error('Error cancelling request:', error);
      toast.error('Erreur lors de l\'annulation de la demande');
    }
  };

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Mon compte
      </Typography>

      <Paper sx={{ mt: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          aria-label="account tabs"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Informations" />
          <Tab label="Réseau" />
          <Tab label="Organisation" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ px: 3 }}>
            {/* Photo de profil */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
              <Box sx={{ position: 'relative' }}>
                <Avatar
                  src={user.profile_photo_url || undefined}
                  sx={{ width: 100, height: 100 }}
                >
                  {!user.profile_photo_url && (
                    <PersonIcon sx={{ fontSize: 60 }} />
                  )}
                </Avatar>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handlePhotoUpload}
                />
                <IconButton
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': { backgroundColor: 'primary.dark' },
                  }}
                  size="small"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingPhoto}
                >
                  {isUploadingPhoto ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <PhotoCameraIcon fontSize="small" />
                  )}
                </IconButton>
              </Box>
              <Box sx={{ ml: 3 }}>
                <Typography variant="h6">
                  {user.first_name} {user.last_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Cliquez sur l'icône pour changer votre photo
                </Typography>
              </Box>
            </Box>

            <Typography variant="h6" gutterBottom>
              Informations personnelles
            </Typography>

            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Nom d'utilisateur"
                value={user.username}
                disabled
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Email"
                value={user.email}
                disabled
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Prénom"
                value={user.first_name}
                disabled
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Nom"
                value={user.last_name}
                disabled
                sx={{ mb: 2 }}
              />
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              Préférences
            </Typography>

            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                select
                label="Fuseau horaire"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                sx={{ mb: 2 }}
              >
                {TIMEZONES.map((tz) => (
                  <MenuItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                label="Adresse IP de la caméra"
                value={cameraIp}
                onChange={(e) => setCameraIp(e.target.value)}
                placeholder="192.168.1.6"
                helperText="L'adresse IP de votre caméra PTZ (ex: 192.168.1.6). Le frontend se connectera directement à cette caméra."
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Taux de taxe (%)"
                type="number"
                value={taxRate}
                onChange={(e) => setTaxRate(e.target.value)}
                placeholder="6"
                helperText="Taux de taxe appliqué sur les factures (par défaut 6%)"
                sx={{ mb: 2 }}
                inputProps={{
                  min: 0,
                  max: 100,
                  step: 0.01
                }}
              />

              <TextField
                fullWidth
                select
                label="Devise"
                value={currency}
                onChange={(e) => setCurrency(e.target.value as 'xpf' | 'eur' | 'usd' | 'aud' | 'sgd' | 'nzd')}
                helperText="Devise utilisée pour la facturation"
                sx={{ mb: 2 }}
              >
                {CURRENCIES.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </TextField>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  disabled={isSaving || isLoading}
                >
                  {isSaving ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </Box>
            </Box>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ px: 3 }}>
            {loadingNetwork ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Demandes reçues */}
                <Typography variant="h6" gutterBottom>
                  Demandes reçues
                </Typography>
                {receivedRequests.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Aucune demande en attente
                  </Typography>
                ) : (
                  <List sx={{ mb: 3 }}>
                    {receivedRequests.map((request) => (
                      <ListItem key={request.id} divider>
                        <ListItemAvatar>
                          <Avatar src={request.from_teacher_data?.profile_photo_url || undefined}>
                            <PersonIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={`${request.from_teacher_data?.first_name} ${request.from_teacher_data?.last_name}`}
                          secondary={request.from_teacher_data?.email}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            color="success"
                            onClick={() => handleAcceptRequest(request.id)}
                            sx={{ mr: 1 }}
                          >
                            <CheckIcon />
                          </IconButton>
                          <IconButton
                            edge="end"
                            color="error"
                            onClick={() => handleRejectRequest(request.id)}
                          >
                            <CloseIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Demandes envoyées */}
                <Typography variant="h6" gutterBottom>
                  Demandes envoyées
                </Typography>
                {sentRequests.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Aucune demande en attente
                  </Typography>
                ) : (
                  <List sx={{ mb: 3 }}>
                    {sentRequests.map((request) => (
                      <ListItem key={request.id} divider>
                        <ListItemAvatar>
                          <Avatar src={request.to_teacher_data?.profile_photo_url || undefined}>
                            <PersonIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={`${request.to_teacher_data?.first_name} ${request.to_teacher_data?.last_name}`}
                          secondary={
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {request.to_teacher_data?.email}
                              <Chip label="En attente" size="small" color="warning" />
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleCancelRequest(request.id)}
                          >
                            Annuler
                          </Button>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Connexions établies */}
                <Typography variant="h6" gutterBottom>
                  Mon réseau ({connections.length})
                </Typography>
                {connections.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Vous n'avez pas encore de connexions. Recherchez des enseignants dans l'onglet Recherche pour les ajouter à votre réseau.
                  </Typography>
                ) : (
                  <List>
                    {connections.map((teacher) => (
                      <ListItem key={teacher.id} divider>
                        <ListItemAvatar>
                          <Avatar src={teacher.profile_photo_url || undefined}>
                            <PersonIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={`${teacher.first_name} ${teacher.last_name}`}
                          secondary={teacher.email}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <OrganizationTab networkConnections={connections} />
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default Account;
